import type { BeaconResponse } from '@skyground/vn-dga'
import { base64urlToBytes, VN_ACCESS_BEACON_PUBLIC_KEY } from '@skyground/vn-dga'

import type { DiscoveredApi } from './beacon-client.js'

import { discoverApiDomain } from './beacon-client.js'
import { generateCandidateDomains } from './candidate-domains.js'

/**
 * The pinned ed25519 public key, decoded once to bytes at module load. This is the client's
 * single trust anchor — a beacon is adopted only if it verifies against this key.
 */
export const PINNED_BEACON_PUBLIC_KEY = base64urlToBytes(VN_ACCESS_BEACON_PUBLIC_KEY)

/** How many DGA candidates a single discovery pass probes before giving up. */
const DEFAULT_WINDOW_SIZE = 8

/**
 * PT-5626: how long a single candidate probe may take before it is abandoned. A blocked domain
 * can be sinkholed to an IP that accepts the TCP connection and then never replies; without a
 * bound, that one dead candidate would stall the whole failover indefinitely. This cap is per
 * candidate, so a hung probe costs at most this long before discovery advances to the next DGA
 * domain.
 */
export const PROBE_TIMEOUT_MS = 4_000

/** What `createTimeoutBeaconFetch` needs from the outside world, injected for testability. */
export interface TimeoutBeaconFetchEnv {
  /**
   * Absolute unix-ms time by which the whole discovery pass must be done (PT-5626). Each probe is
   * bounded by whichever is sooner — its own `timeoutMs` or the time left in this budget — so once
   * the budget is spent every remaining candidate is abandoned without another network attempt.
   * Omit for an unbounded pass in which only the per-probe timeout applies.
   */
  deadlineAt?: number
  /** The underlying fetch implementation (the global `fetch` in production). */
  fetch: typeof fetch
  /** Current unix ms, injected so the overall-budget arithmetic stays deterministic in tests. */
  now?: () => number
  /** Per-probe deadline in ms; the probe is aborted once it elapses. */
  timeoutMs: number
}

/**
 * PT-5626: build a `fetchBeacon` that bounds every probe with its own `AbortController` and,
 * together, the whole pass with an overall time budget.
 *
 * A timer is armed per call; if the probe has not settled by its effective deadline — the sooner
 * of `timeoutMs` and the time still left in the overall `deadlineAt` budget — the controller
 * aborts the request, `fetch` rejects, and, because `discoverApiDomain` treats any rejection as an
 * unreachable candidate, discovery moves straight on to the next DGA domain. Once the overall
 * budget is already spent, a probe rejects immediately without touching the network, so the rest
 * of the window unwinds at once instead of paying another timeout each. This guarantees a hung or
 * sinkholed origin can never stall failover, and a window full of them can never outlast the
 * budget. The timer is always cleared (even on the honest fast path) so no probe leaks.
 *
 * The security posture is unchanged: `no-store` keeps this challenge-response off the browser
 * cache, `omit` sends no cookies, and a non-2xx (block-page / sinkhole 403) throws so the
 * candidate is skipped. Trust is still decided solely by the pinned-key signature downstream.
 */
export const createTimeoutBeaconFetch =
  (env: TimeoutBeaconFetchEnv) =>
  async (url: string): Promise<BeaconResponse> => {
    const now = env.now ?? ((): number => Date.now())
    const budgetLeft = env.deadlineAt === undefined ? Infinity : env.deadlineAt - now()
    if (budgetLeft <= 0) {
      // Overall discovery budget spent — abandon without another network attempt so the remaining
      // window collapses immediately rather than costing a full timeout per leftover candidate.
      throw new DOMException('beacon discovery budget exhausted', 'TimeoutError')
    }

    const controller = new AbortController()
    const timer = setTimeout(
      () => controller.abort(new DOMException('beacon probe timed out', 'TimeoutError')),
      Math.min(env.timeoutMs, budgetLeft),
    )

    try {
      const response = await env.fetch(url, { cache: 'no-store', credentials: 'omit', signal: controller.signal })
      if (!response.ok) throw new Error(`beacon responded ${response.status}`)
      return (await response.json()) as BeaconResponse
    } finally {
      clearTimeout(timer)
    }
  }

/**
 * Discover a live, trusted API domain by probing a window of DGA candidates from `startIndex`.
 * Wires the browser's real `fetch` and clock into the pure `discoverApiDomain` trust-gate.
 * Returns the adopted origin (and its DGA index, for the caller to persist and resume from) or
 * `null` if no candidate in the window proves itself before the overall budget is spent.
 */
export const discoverLiveApi = (
  startIndex = 0,
  windowSize = DEFAULT_WINDOW_SIZE,
  // Default the ceiling to a full sweep of THIS window so a larger custom window is never
  // truncated either (for the default window this equals DISCOVERY_BUDGET_MS).
  budgetMs = windowSize * PROBE_TIMEOUT_MS,
): Promise<DiscoveredApi | null> => {
  const now = (): number => Date.now()
  return discoverApiDomain({
    candidates: generateCandidateDomains(startIndex, windowSize),
    now,
    publicKey: PINNED_BEACON_PUBLIC_KEY,
    // PT-5626: each probe is bounded so a hung/sinkholed domain can never stall failover, and the
    // shared `deadlineAt` caps the whole window so a sinkhole-filled pass can never outlast the budget.
    fetchBeacon: createTimeoutBeaconFetch({
      deadlineAt: now() + budgetMs,
      now,
      timeoutMs: PROBE_TIMEOUT_MS,
      fetch: (...args) => fetch(...args),
    }),
  })
}
